import { getValidAccessToken } from '../../../lib/supabase';
import type { AdminListResponse } from '../../../domain/superadmin/types';
import type { SupportTicketDetail, SupportTicketRow } from '../../../domain/support/types';
import { SuperadminApiError } from '../superadmin/errors';

type SupportListParams = {
  page?: number;
  pageSize?: number;
  status?: string;
};

async function supportFetch<T>(
  path: string,
  params?: SupportListParams & { organizationId?: string; id?: string }
): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`);
  if (params?.organizationId) url.searchParams.set('organizationId', params.organizationId);
  if (params?.id) url.searchParams.set('id', params.id);
  if (params?.page) url.searchParams.set('page', String(params.page));
  if (params?.pageSize) url.searchParams.set('pageSize', String(params.pageSize));
  if (params?.status) url.searchParams.set('status', params.status);

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API de suporte. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API de suporte',
      response.status,
      err.code
    );
  }

  return response.json();
}

async function supportJsonRequest<T>(
  method: 'POST',
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API de suporte. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API de suporte',
      response.status,
      err.code
    );
  }

  return response.json();
}

export function fetchSupportTickets(
  organizationId: string,
  params: SupportListParams = {}
): Promise<AdminListResponse<SupportTicketRow>> {
  return supportFetch('support-list-tickets', { organizationId, ...params });
}

export function fetchSupportTicket(
  organizationId: string,
  id: string
): Promise<SupportTicketDetail> {
  return supportFetch('support-get-ticket', { organizationId, id });
}

export function createSupportTicket(
  organizationId: string,
  subject: string,
  body: string
): Promise<SupportTicketDetail> {
  return supportJsonRequest('POST', 'support-create-ticket', { organizationId, subject, body });
}

export function replySupportTicket(
  organizationId: string,
  ticketId: string,
  body: string
): Promise<SupportTicketDetail> {
  return supportJsonRequest('POST', 'support-reply-ticket', { organizationId, ticketId, body });
}
